<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useFloating } from '@/composables/useFloating.js'
import { useDirectory } from '@/stores/directory.js'
import { useSession } from '@/stores/session.js'
import { fold } from '@addictivegroup/addictive-offline'
import UiAvatar from '@/components/ui/UiAvatar.vue'

/**
 * Owner: a person or a group (D-06). v-model is {type, uuid, label} or null. The list floats over
 * the page by the field — above it when there is more room there (a field at the screen's foot).
 */
const props = defineProps({
  modelValue: { type: Object, default: null },
  disabled: Boolean,
  allowGroups: { type: Boolean, default: true },
  usersOnly: Boolean,
  placeholder: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])
const directory = useDirectory()
const session = useSession()
const query = ref('')
const { open, anchor, floating, style, toggle, close, place } = useFloating({ fit: () => true, onClose: () => (query.value = '') })

onMounted(() => directory.load().catch(() => null))

const options = computed(() => {
  const q = fold(query.value)
  const users = directory.users.filter((u) => !q || fold(u.label).includes(q)).map((u) => ({ type: 'user', uuid: u.uuid, label: u.label, hint: u.job_title }))
  const groups = props.allowGroups && !props.usersOnly ? directory.groups.filter((g) => !q || fold(g.label).includes(q)).map((g) => ({ type: 'group', uuid: g.uuid, label: g.label, hint: '' })) : []
  const me = session.me ? users.filter((u) => u.uuid === session.me.uuid) : []
  return [...me, ...users.filter((u) => u.uuid !== session.me?.uuid), ...groups].slice(0, 40)
})

function pick(o) {
  emit('update:modelValue', { type: o.type, uuid: o.uuid, label: o.label })
  close()
}
// Fewer found, a smaller list: placed again, so one opened above stays by its field.
watch(options, () => open.value && nextTick(place))
</script>

<template>
  <div ref="anchor" class="relative">
    <button type="button" class="input flex items-center gap-2 text-left" :disabled="disabled" data-testid="owner-picker" @click="toggle">
      <template v-if="modelValue">
        <UiAvatar :label="modelValue.label || ''" size="sm" :group="modelValue.type === 'group'" />
        <span class="min-w-0 flex-1 truncate">{{ modelValue.label }}</span>
      </template>
      <span v-else class="flex-1 text-faint">{{ placeholder || $t('common.select') }}</span>
      <i class="fa-solid fa-chevron-down text-xs text-faint" aria-hidden="true"></i>
    </button>
    <Teleport to="body">
    <div v-if="open" ref="floating" :style="style" class="surface fixed flex flex-col overflow-hidden rounded-xl border border-default shadow-xl" data-testid="owner-picker-list">
      <input v-model="query" type="search" class="input shrink-0 rounded-b-none border-0 border-b" :placeholder="$t('common.searchPlaceholder')" autofocus />
      <div class="max-h-64 min-h-0 flex-1 overflow-y-auto py-1">
        <button v-for="o in options" :key="o.type + o.uuid" type="button" class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-[var(--surface-3)]" @click="pick(o)">
          <UiAvatar :label="o.label" size="sm" :group="o.type === 'group'" />
          <span class="min-w-0 flex-1 truncate">{{ o.label }}<span v-if="o.uuid === session.me?.uuid" class="text-faint"> ({{ $t('common.me') }})</span></span>
          <span class="text-xs text-faint">{{ o.hint || (o.type === 'group' ? $t('entities.group.one') : '') }}</span>
        </button>
      </div>
    </div>
    </Teleport>
  </div>
</template>
